import type { ApplicationStatus } from '../types'

/**
 * Status transition machine for job applications.
 * Defines which statuses can transition to which other statuses.
 * Supports "undo" transitions for common mistakes and real-world scenarios.
 *
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │  STATUS           │ AVAILABLE ACTIONS                                       │
 * ├───────────────────┼─────────────────────────────────────────────────────────┤
 * │  saved            │ Mark Applied, Start Process, Withdraw                   │
 * │  applied          │ Undo → Saved, Start Process, Ghosted, Withdraw, Reject  │
 * │  in_process       │ Undo → Applied, Got Offer, Ghosted, Withdraw, Reject    │
 * │  offer            │ Undo → In Process, Accept, Decline, Reject              │
 * │  accepted         │ Undo → Offer, Mark Hired                                │
 * │  offer_declined   │ Undo → Offer (changed mind)                             │
 * │  ghosted          │ Resume → In Process, Withdraw, Reject                   │
 * │  rejected         │ Resume → In Process (company reconsidered)              │
 * │  withdrawn        │ — (terminal)                                            │
 * │  hired            │ — (terminal)                                            │
 * │  archived         │ — (special: any status can be archived/unarchived)      │
 * └─────────────────────────────────────────────────────────────────────────────┘
 */

export const STATUS_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  saved: ['applied', 'in_process', 'withdrawn'],
  applied: ['saved', 'in_process', 'ghosted', 'withdrawn', 'rejected'],
  in_process: ['applied', 'offer', 'ghosted', 'withdrawn', 'rejected'],
  offer: ['in_process', 'accepted', 'offer_declined', 'rejected'],
  accepted: ['offer', 'hired'],
  offer_declined: ['offer'], // can change mind
  ghosted: ['in_process', 'withdrawn', 'rejected'],
  rejected: ['in_process'], // company may reconsider
  withdrawn: [], // terminal - user's decision
  hired: [], // terminal - process complete
  archived: [], // special - handled separately via archive/unarchive actions
}

/**
 * Status labels for display
 */
export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  saved: 'Saved',
  applied: 'Applied',
  in_process: 'In progress',
  offer: 'Offer',
  accepted: 'Accepted',
  offer_declined: 'Declined',
  ghosted: 'Ghosted',
  withdrawn: 'Withdrawn',
  rejected: 'Rejected',
  hired: 'Hired',
  archived: 'Archived',
}

/**
 * Get valid next statuses for a given current status
 */
export const getValidTransitions = (currentStatus: ApplicationStatus): ApplicationStatus[] => {
  return STATUS_TRANSITIONS[currentStatus] ?? []
}

/**
 * Check if a transition from one status to another is valid
 */
export const isValidTransition = (
  fromStatus: ApplicationStatus,
  toStatus: ApplicationStatus
): boolean => {
  // Same status is always "valid" (no change)
  if (fromStatus === toStatus) return true

  // Archived is a special case - handled by archive/unarchive actions, not status change
  if (toStatus === 'archived') return false

  const validNextStatuses = STATUS_TRANSITIONS[fromStatus]
  return validNextStatuses?.includes(toStatus) ?? false
}

/**
 * Check if a status is terminal (no further transitions possible)
 */
export const isTerminalStatus = (status: ApplicationStatus): boolean => {
  const transitions = STATUS_TRANSITIONS[status]
  return !transitions || transitions.length === 0
}

/**
 * Get all statuses that can transition TO a given status
 */
export const getStatusSources = (targetStatus: ApplicationStatus): ApplicationStatus[] => {
  const sources: ApplicationStatus[] = []
  for (const [status, transitions] of Object.entries(STATUS_TRANSITIONS)) {
    if (transitions.includes(targetStatus)) {
      sources.push(status as ApplicationStatus)
    }
  }
  return sources
}
